/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    classNamesFunction,
    FontWeights,
    IButtonProps,
    IButtonStyles,
    IconButton,
    IDropdownOption,
    Label,
    Spinner,
    Stack
} from '@fluentui/react';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import { useLocalStore } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useFormState } from 'src/core/hooks/useFormState';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { FormValidators } from 'src/core/utils/formValidators';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { AMLConnectionInfo } from 'src/customText/core/stores/CustomTextProjectStore';
import { AMLConnectionSteps } from 'src/customText/verticals/azureMLConnectionModal/AzureMLConnectionModal';
import { AzureMLTabConnectionFooter } from 'src/customText/verticals/azureMLConnectionModal/components/AzureMLTabConnectionFooter';
import { useStorageAccountRoles } from 'src/customText/verticals/azureMLConnectionModal/hooks/useStorageAccountRoles';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import styled from 'styled-components';

type ConnectionPageProps = {
    onNext: (amlConnectionInfo: Partial<AMLConnectionInfo>) => void;
    onDismiss: () => void;
    onPrevious: () => void;
    amlConnectionInfo: AMLConnectionInfo;
};

const messages = defineMessages({
    title: {
        id: 'customText.connectionPage.title',
        defaultMessage: 'Create a new Azure Machine Learning project'
    },
    subtitle: {
        id: 'customText.connectionPage.subtitle',
        defaultMessage:
            'Create a new project in Azure Machine Learning within an existing workspace, or go to Azure Machine Learning to create a new workspace.'
    },
    title2: {
        id: 'customText.connectionPage.title2',
        defaultMessage: 'Detected settings'
    },
    subscription: {
        id: 'customText.connectionPage.subscription',
        defaultMessage: 'Subscription:'
    },

    storageAccount: {
        id: 'customText.connectionPage.storageAccount',
        defaultMessage: 'Storage Account:'
    },
    azureBlobDatastore: {
        id: 'customText.connectionPage.azureBlobDatastore',
        defaultMessage: 'Azure Blob Datastore:'
    },
    labelingTaskType: {
        id: 'customText.connectionPage.labelingTaskType',
        defaultMessage: 'Labeling Task type:'
    },
    language: {
        id: 'customText.connectionPage.language',
        defaultMessage: 'Language:'
    },
    incremental: {
        id: 'customText.connectionPage.incremental',
        defaultMessage: 'Incremental refresh:'
    },
    projectSettings: {
        id: 'customText.connectionPage.projectSettings',
        defaultMessage: 'Project settings'
    },
    projectSettingsDesc: {
        id: 'customText.connectionPage.projectSettingsDesc',
        defaultMessage:
            'To label with Azure Machine Learning you need to create a project, which must be assigned to an Azure Machine Learning workspace.'
    },
    azureMLWorkspace: {
        id: 'customText.connectionPage.azureMLWorkspace',
        defaultMessage: 'Azure Machine Learning workspace'
    },
    workspacePlaceholder: {
        id: 'customText.connectionPage.workspacePlaceholder',
        defaultMessage: 'Select workspace'
    },
    emptyItemPlaceholder: {
        id: 'customText.connectionPage.emptyItemPlaceholder',
        defaultMessage: 'No items found'
    },
    workspaceDesc1: {
        id: 'customText.connectionPage.workspaceDesc1',
        defaultMessage: 'Select one of the listed workspaces that shares the same Azure Storage account'
    },
    workspaceDesc2: {
        id: 'customText.connectionPage.workspaceDesc2',
        defaultMessage: 'as the Language resource or'
    },
    externalLinkPlaceholder: {
        id: 'customText.connectionPage.externalLinkPlaceholder',
        defaultMessage: 'Create a new workspace'
    },
    azureMLProjectName: {
        id: 'customText.connectionPage.azureMLProjectName',
        defaultMessage: 'Azure Machine Learning project name'
    },
    namePlaceholder: {
        id: 'customText.connectionPage.namePlaceholder',
        defaultMessage: 'Enter a name for your project'
    },
    textNER: {
        id: 'customText.connectionPage.textNER',
        defaultMessage: 'Text Named Entity Recognition'
    },
    textClassification: {
        id: 'customText.connectionPage.textClassification',
        defaultMessage: 'Text Classification'
    },
    enabled: {
        id: 'customText.connectionPage.enabled',
        defaultMessage: 'Enabled'
    },
    invalidProjName: {
        id: 'customText.connectionPage.invalidProjName',
        defaultMessage: 'invalid Project Name'
    },
    validationRunning: {
        id: 'customText.connectionPage.validationRunning',
        defaultMessage: 'Validating project name'
    }
});

const StyledDropdown = styled(LuisDropdown)<{ rootWidth: string }>(props => ({
    width: props.rootWidth
}));

const StyledTextField = styled(LuisTextField)<{ isLargeOrGreater: boolean }>(props => ({
    width: props.isLargeOrGreater ? '300px' : '100%'
}));

const childrenGapConstant = 4;
const stepTheme = getScopedTheme('getAmlWorkSpaces');
const refreshButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    iconDisabled: { color: stepTheme.disableColor },
    rootPressed: { backgroundColor: 'transparent' },
    rootHovered: { backgroundColor: 'transparent' },
    rootDisabled: { backgroundColor: 'transparent' },
    root: {
        marginTop: '30px'
    }
});

export const ConnectionPage = observer((props: ConnectionPageProps) => {
    const { onNext, onDismiss, onPrevious, amlConnectionInfo } = props;
    const { localize } = useLocalization();
    const { isLargeOrGreater } = useResponsiveMode();
    const { rootStore, dispatcher } = useGlobalState();
    const validateProjectNameTracker = useProgressTracker({
        persistOnComplete: true
    });
    const [workspaceRegion, setWorkspaceRegion] = React.useState(amlConnectionInfo.workspaceRegion ?? '');
    const [workspaceId, setWorkSpaceId] = React.useState('');
    const { csAccountStore, subscriptionId } = rootStore.azureStore.session;
    const containerPermission = useStorageAccountRoles(workspaceId);
    const storageAccount = csAccountStore.ownedStorageAccountInfo.name;
    const storageResourceGroup = csAccountStore.ownedStorageAccountInfo.resourceGroupName;
    const { containerName, projectKind, amlWorkspaceContainerName } = rootStore.currentCustomTextProjectStore;
    const { subscriptionStoreHouse } = rootStore.azureStore;
    const azureAccountsStoreHouse = useLocalStore<ResourceStoreHouse<AzureCsAccountStore>>(createResourceStoreHouse);
    const subscription = subscriptionStoreHouse.findById(subscriptionId)?.name ?? '-';
    const isExtractionProject = projectKind === 'CustomEntityRecognition';
    const labelingTaskType = isExtractionProject ? localize(messages.textNER) : localize(messages.textClassification);

    const { formState, updateFormState } = useFormState<Partial<AMLConnectionInfo>>({
        projectName: [
            amlConnectionInfo.projectName ?? '',
            FormValidators.createRequiredValidator(),
            FormValidators.createBlacklistValidator({
                mode: 'include',
                errorMessage: messages.invalidProjName,
                characterList: LuisConstants.ResourceLimits.AmlAllowedNameReges
            })
        ],
        azureMLWorkspace: [amlConnectionInfo?.azureMLWorkspace ?? '', FormValidators.createRequiredValidator()],
        projectRoute: [amlConnectionInfo?.projectRoute ?? '', FormValidators.createRequiredValidator()]
    });

    const currentProject = rootStore.currentCustomTextProjectStore;
    const projectLanguage =
        rootStore.customTextCultureStorehouse.items.find(c => c.code === currentProject.culture)?.name ?? currentProject.culture;

    const loadAmlWorkspaces = () => {
        dispatcher.dispatch('getAzureAmlWorkspacesBySubscriptionId', {
            id: subscriptionStoreHouse.findById(subscriptionId).id,
            localStoreHouse: azureAccountsStoreHouse
        });
    };

    const handleProjectName = (projectName: string) => {
        updateFormState({ projectName });
        validateProjectNameTracker.setState('notStarted');
    };

    const handleNext = () => {
        dispatcher.dispatch('validateProjectName', {
            projectName: formState.fields.projectName.value,
            projectRoute: formState.fields.projectRoute.value,
            workspaceRegion,
            tracker: validateProjectNameTracker
        });
    };

    React.useEffect(() => {
        loadAmlWorkspaces();
    }, []);

    React.useEffect(() => {
        if (validateProjectNameTracker.isCompleted) {
            onNext({
                subscription,
                storageAccount,
                containerName,
                labelingTaskType,
                projectLanguage,
                azureMLWorkspace: formState.fields.azureMLWorkspace.value,
                projectName: formState.fields.projectName.value,
                subscriptionId,
                storageResourceGroup,
                projectRoute: formState.fields.projectRoute.value,
                amlWorkspaceContainerName,
                workspaceRegion
            });
        }
    }, [validateProjectNameTracker.state]);
    const workspacesOptions: IDropdownOption[] = azureAccountsStoreHouse.items
        .filter(item => item.ownedStorageAccountInfo.name === storageAccount)
        .map(c => ({ key: c.id, text: c.name }));

    const labelOnRender = (labelText: string, tooltipContent?: string | React.ReactNode, isRequired: boolean = true) => (
        <Stack horizontal verticalAlign="center">
            <Label styles={{ root: { selectors: { '::after': { paddingRight: 0 } } } }} required={isRequired}>
                {labelText}
            </Label>
            {tooltipContent && <CalloutButton isDarkMode={false} content={tooltipContent} />}
        </Stack>
    );

    const handleChooseWorkspace = (_: any, val: IDropdownOption<any>) => {
        const workspace = azureAccountsStoreHouse.items.find(item => item.name === val.text);
        setWorkspaceRegion(workspace.region);
        setWorkSpaceId(workspace.principalId);
        updateFormState({ azureMLWorkspace: val.text as string, projectRoute: val.key as string });
        dispatcher.dispatch('getAmlWorkspaceInfo', { projectRoute: val.key as string, workspaceRegion: workspace.region });
    };

    const isModalValid = Boolean(formState.form.valid) && !containerPermission.error;
    const isProjectNameValid = !formState.fields.projectName.valid
        ? formState.fields.projectName.errorMessage
        : validateProjectNameTracker.isFailed
        ? validateProjectNameTracker.error.message
        : null;

    return (
        <Stack styles={{ root: { minHeight: '630px' } }}>
            <Stack
                styles={{ root: { maxHeight: '70vh', overflowY: 'auto' } }}
                tokens={{ childrenGap: 12, padding: `${isLargeOrGreater ? `0 0 0 32px` : '12px 20px'}` }}>
                <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                    {localize(messages.title)}
                </LuisText>
                <LuisText>{localize(messages.subtitle)}</LuisText>
                <br />
                <Stack horizontal verticalAlign="center">
                    <Label styles={{ root: { selectors: { '::after': { paddingRight: 0 } } } }} required>
                        {localize(messages.title2)}
                    </Label>
                    <CalloutButton content={localize(messages.title2)} />
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText>{localize(messages.subscription)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {subscription}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText>{localize(messages.storageAccount)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {storageAccount}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText>{localize(messages.azureBlobDatastore)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {containerName}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText>{localize(messages.labelingTaskType)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {labelingTaskType}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText>{localize(messages.language)}</LuisText>
                    <LuisText>
                        &nbsp;
                        {projectLanguage}
                    </LuisText>
                </Stack>
                <Stack horizontal tokens={{ childrenGap: childrenGapConstant }}>
                    <LuisText>{localize(messages.incremental)}</LuisText>
                    <LuisText>&nbsp; {localize(messages.enabled)}</LuisText>
                </Stack>
                <Stack horizontal verticalAlign="center">
                    <Label styles={{ root: { selectors: { '::after': { paddingRight: 0 } } } }} required>
                        {localize(messages.projectSettings)}
                    </Label>
                    <CalloutButton content={localize(messages.title2)} />
                </Stack>

                <LuisText>{localize(messages.projectSettingsDesc)}</LuisText>

                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <StyledDropdown
                        options={workspacesOptions}
                        selectedKey={formState.fields.projectRoute.value}
                        isLoading={containerPermission.isLoading || azureAccountsStoreHouse.tracker.state === 'running'}
                        rootWidth={isLargeOrGreater ? '300px' : '90%'}
                        ariaLabel={localize(messages.azureMLWorkspace)}
                        onChange={handleChooseWorkspace}
                        placeholder={localize(messages.workspacePlaceholder)}
                        emptyStatePlaceholder={localize(messages.emptyItemPlaceholder)}
                        onRenderLabel={() => labelOnRender(localize(messages.azureMLWorkspace))}
                        errorMessage={containerPermission.error}
                    />
                    <IconButton
                        onClick={loadAmlWorkspaces}
                        styles={refreshButtonStyles}
                        iconProps={{ iconName: 'Refresh' }}
                        ariaLabel={'Refresh'}
                        disabled={azureAccountsStoreHouse.tracker.isRunning}
                    />
                </Stack>
                <LuisText>
                    {localize(messages.workspaceDesc1)}
                    &nbsp;
                    <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}> {storageAccount} </LuisText>
                    &nbsp;
                    {localize(messages.workspaceDesc2)}
                    &nbsp;
                    <ExternalLink href={'https://ms.portal.azure.com/#create/Microsoft.MachineLearningServices'}>
                        {localize(messages.externalLinkPlaceholder)}
                    </ExternalLink>
                </LuisText>
                <Stack horizontal tokens={{ childrenGap: 8 }}>
                    <StyledTextField
                        required
                        disabled={!formState.fields.azureMLWorkspace.valid}
                        maxLength={50}
                        isLargeOrGreater={isLargeOrGreater}
                        label={localize(messages.azureMLProjectName)}
                        value={formState.fields.projectName.value}
                        placeholder={localize(messages.namePlaceholder)}
                        onChange={(_, val) => handleProjectName(val)}
                        errorMessage={isProjectNameValid}
                    />
                    {validateProjectNameTracker.state === 'running' ? (
                        <Stack styles={{ root: { marginTop: '34px' } }}>
                            <Spinner label={localize(messages.validationRunning)} ariaLive="assertive" labelPosition="right" />
                        </Stack>
                    ) : null}
                </Stack>
            </Stack>
            <AzureMLTabConnectionFooter
                onNext={handleNext}
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                isValid={isModalValid}
                activeStep={AMLConnectionSteps.Connection}
            />
        </Stack>
    );
});
